import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { isTouchDevice } from '@investoro/core';
import { UserActions } from 'store/User/Actions';
import { useUser } from 'store/User/Context';
import styled from 'styled-components';
import { performLogout } from 'utils/auth/performLogout';
import { Platform, usePlatform } from 'utils/hooks/usePlatform';
import { hasRole, UserRole } from 'utils/userRoleHelper';

import { IconEnum } from 'components/common/Icon';
import Icon from 'components/common/Icon';

const SIDEBAR_WIDTH = 280;
const ICON_ONLY_WIDTH = 76;

const SECTION_ROUTES: Record<string, string[]> = {
    moduly: [
        '/',
        '/rent',
        '/auction',
        '/tender',
        '/cooperative',
        '/bailiff-notices'
    ],
    marketplace: [
        '/user',
        '/my-offers',
        '/add-offer',
        '/messages'
    ],
    moje: ['/favorites', '/chatbot-messages'],
    posrednik: ['/broker'],
    analityka: [
        '/analysis',
        '/calculator',
        '/analytic-panel',
        '/property-valuation'
    ],
    konto: ['/my-settings', '/calculator-settings']
};

const SECTION_ROUTE_PAIRS = Object.entries(SECTION_ROUTES)
    .flatMap(([section, routes]) => routes.map((route) => ({ section, route })))
    .sort((a, b) => b.route.length - a.route.length);

const getSectionForPath = (pathname: string): string | null => {
    for (const { section, route } of SECTION_ROUTE_PAIRS) {
        if (route === '/' ? pathname === '/' : pathname === route || pathname.startsWith(`${route}/`)) {
            return section;
        }
    }

    return null;
};

const SidebarWrapper = styled.nav<{ $width: number }>`
    position: fixed;
    top: 20px;
    left: 15px;
    width: ${({ $width }) => $width}px;
    height: calc(100vh - 40px);
    background-color: var(--color-white);
    border-radius: var(--box-border-radius);
    box-shadow: 0 2px 16px rgba(0, 0, 0, 0.07);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transition: width 0.22s ease;
    z-index: 100;
    transform: translateZ(0);
`;

const LogoArea = styled.div<{ $centered?: boolean }>`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: ${({ $centered }) => $centered ? '8px' : '18px 16px 12px'};
    justify-content: ${({ $centered }) => $centered ? 'center' : 'flex-start'};
    border-bottom: 1px solid var(--color-alt-second);
    flex-shrink: 0;
`;

const LogoImg = styled.img<{ $clickable: boolean }>`
    height: 28px;
    cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
`;

const LogoImgFavicon = styled(LogoImg)`
    height: 60px;
`;

const ScrollArea = styled.div`
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 8px 0 16px;
    scrollbar-width: thin;
    scrollbar-color: var(--color-scroll) transparent;

    &::-webkit-scrollbar {
        width: 4px;
    }
    &::-webkit-scrollbar-thumb {
        background: var(--color-scroll);
        border-radius: 4px;
    }
`;

const SectionHeader = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 10px 16px 4px;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--color-sidebar-section-header);
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    text-align: left;
    transition: color 0.15s, background-color 0.15s;

    .icon { width: 14px; height: 14px; }
    img { width: 14px; height: 14px; }

    &:hover {
        color: var(--color-sidebar-accent);
        background-color: var(--color-sidebar-hover-soft);
        border-radius: 6px;
    }
`;

const ChevronIcon = styled.span<{ $open: boolean }>`
    display: inline-flex;
    margin-left: auto;
    flex-shrink: 0;
    opacity: 0.5;
    transition: transform 0.2s ease;
    transform: ${({ $open }) => $open ? 'rotate(0deg)' : 'rotate(-90deg)'};
`;

const SectionItems = styled.div<{ $open: boolean }>`
    overflow: hidden;
    max-height: ${({ $open }) => $open ? '600px' : '0'};
    transition: max-height 0.3s ease;
`;

const NavLabel = styled.span<{ $color?: string }>`
    font-size: 12px;
    font-weight: 500;
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${({ $color }) => $color ?? 'var(--color-primary)'};
`;

const NavItem = styled(NavLink)`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 16px;
    margin: 1px 8px;
    border-radius: 8px;
    text-decoration: none;
    transition: background-color 0.15s ease;
    white-space: nowrap;
    overflow: hidden;
    border-left: 3px solid transparent;

    .icon { width: 16px; height: 16px; }
    img { width: 16px; height: 16px; }

    &:hover {
        background-color: var(--color-sidebar-hover);
        text-decoration: none;

        ${NavLabel} { color: var(--color-sidebar-text-active); }
    }

    &.active {
        background-color: var(--color-sidebar-active);
        border-left-color: var(--color-sidebar-accent);

        ${NavLabel} {
            color: var(--color-sidebar-text-active);
            font-weight: 600;
        }
    }
`;

const NavButton = styled.button`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 16px;
    margin: 1px 8px;
    border-radius: 8px;
    border: none;
    background: none;
    cursor: pointer;
    transition: background-color 0.15s ease;
    white-space: nowrap;
    overflow: hidden;
    width: calc(100% - 16px);
    text-align: left;

    .icon { width: 16px; height: 16px; }
    img { width: 16px; height: 16px; }

    &:hover {
        background-color: var(--color-sidebar-hover);

        ${NavLabel} { color: var(--color-sidebar-text-active); }
    }
`;

const HighlightBadge = styled.span`
    margin-left: auto;
    background-color: var(--color-alt);
    color: #fff;
    font-size: 9px;
    font-weight: 700;
    padding: 1px 6px;
    border-radius: 20px;
    letter-spacing: 0.05em;
`;

const Divider = styled.div`
    height: 1px;
    background-color: var(--color-alt-second);
    margin: 6px 16px;
`;

const IconStripScroll = styled.div`
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 8px 4px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 2px;
    scrollbar-width: none;

    &::-webkit-scrollbar { display: none; }
`;

const CollapsedSectionBtn = styled.button<{ $active: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 8px 4px;
    border: none;
    background: ${({ $active }) => $active ? 'var(--color-sidebar-active)' : 'none'};
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 0.15s;
    flex-shrink: 0;

    .icon { width: 20px; height: 20px; }
    img {
        width: 20px;
        height: 20px;
        transform: translateZ(0);
        backface-visibility: hidden;
    }

    &:hover {
        background-color: var(--color-sidebar-hover-strong);
    }
`;

const CollapsedSectionLabel = styled.span<{ $active: boolean }>`
    font-size: 9px;
    font-weight: 600;
    color: ${({ $active }) => $active ? 'var(--color-sidebar-accent)' : 'var(--color-primary)'};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    line-height: 1;
    white-space: nowrap;
    transition: color 0.15s;
`;

const DrawerOverlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 99;
`;

const CollapseButton = styled.button<{ $collapsed: boolean }>`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    background: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background-color 0.15s;
    margin-left: auto;

    &:hover { background-color: var(--color-alt-second); }

    .icon, img {
        width: 14px;
        height: 14px;
        transition: transform 0.22s ease;
        transform: ${({ $collapsed }) => $collapsed ? 'rotate(0deg)' : 'rotate(180deg)'};
    }
`;

const CSS_VAR = '--nav-desktop-width';

const NavigationDesktop: FC = () => {
    const { user, dispatch } = useUser();
    const platform = usePlatform();
    const location = useLocation();
    const isDarkMode = user.isDarkMode;
    const hasCalculationRole = hasRole(UserRole.CALCULATOR, user.userRole);
    const hasPredictPriceRole = hasRole(UserRole.PREDICT_PRICE, user.userRole);
    const isWordpressUser = user.isWordpressUser;

    const navigate = useNavigate();

    const [collapsed, setCollapsed] = useState<boolean>(true);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [openSection, setOpenSection] = useState<string | null>(() => getSectionForPath(window.location.pathname));
    const [isTouch] = useState(() => isTouchDevice());
    const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const sidebarRef = useRef<HTMLElement>(null);

    useEffect(() => {
        const w = collapsed ? `${ICON_ONLY_WIDTH + 20}px` : `${SIDEBAR_WIDTH + 30}px`;
        document.documentElement.style.setProperty(CSS_VAR, w);
    }, [collapsed]);

    useEffect(() => () => {
        document.documentElement.style.setProperty(CSS_VAR, '0px');
        if (leaveTimer.current) clearTimeout(leaveTimer.current);
    }, []);

    useEffect(() => {
        if (!collapsed) setDrawerOpen(false);
    }, [collapsed]);

    useEffect(() => {
        const section = getSectionForPath(location.pathname);

        if (section) setOpenSection(section);
    }, [location.pathname]);

    const handleMouseEnter = useCallback(() => {
        if (!collapsed) return;
        if (leaveTimer.current) clearTimeout(leaveTimer.current);
        setDrawerOpen(true);
    }, [collapsed]);

    const handleMouseLeave = useCallback((e: React.MouseEvent) => {
        const rect = sidebarRef.current?.getBoundingClientRect();

        if (rect && e.clientX > rect.left && e.clientX < rect.right && e.clientY > rect.top && e.clientY < rect.bottom) {
            return;
        }

        leaveTimer.current = setTimeout(() => {
            if (sidebarRef.current?.matches(':hover')) return;
            setDrawerOpen(false);
        }, 80);
    }, []);

    const toggleSection = useCallback((id: string) => {
        setOpenSection((prev) => prev === id ? null : id);
    }, []);

    const toggleCollapsed = useCallback(() => {
        setCollapsed((prev) => !prev);
    }, []);

    const logout = useCallback(() => performLogout({
        dispatch,
        isWordpressUser,
        platform
    }), [
        dispatch,
        isWordpressUser,
        platform
    ]);

    const navContent = useMemo(() => <ScrollArea>
        <SectionHeader onClick={() => toggleSection('moduly')}>
            <Icon icon={IconEnum.HOUSE} />
            Moduły
            <ChevronIcon $open={openSection === 'moduly'}>
                <Icon icon={IconEnum.ACCORDION_DOWN} />
            </ChevronIcon>
        </SectionHeader>
        <SectionItems $open={openSection === 'moduly'}>
            <NavItem to="/" end>
                <Icon icon={IconEnum.HOUSE} />
                <NavLabel>Sprzedaż</NavLabel>
            </NavItem>
            <NavItem to="/rent">
                <Icon icon={IconEnum.HOUSE_CIRCLE} />
                <NavLabel>Wynajem</NavLabel>
            </NavItem>
            <NavItem to="/auction">
                <Icon icon={IconEnum.GAVEL} />
                <NavLabel>Licytacje komornicze</NavLabel>
            </NavItem>
            <NavItem to="/tender">
                <Icon icon={IconEnum.BUILDING_COLUMNS} />
                <NavLabel>Przetargi</NavLabel>
            </NavItem>
            <NavItem to="/cooperative">
                <Icon icon={IconEnum.MAP_PIN} />
                <NavLabel>Przetargi lokalne</NavLabel>
            </NavItem>
            <NavItem to="/bailiff-notices">
                <Icon icon={IconEnum.TRIANGLE_WARNING} />
                <NavLabel>Informacje o zadłużeniach</NavLabel>
            </NavItem>
        </SectionItems>

        <Divider />

        <SectionHeader onClick={() => toggleSection('marketplace')}>
            <Icon icon={IconEnum.USER} />
            Marketplace
            <ChevronIcon $open={openSection === 'marketplace'}>
                <Icon icon={IconEnum.ACCORDION_DOWN} />
            </ChevronIcon>
        </SectionHeader>
        <SectionItems $open={openSection === 'marketplace'}>
            <NavItem to="/user">
                <Icon icon={IconEnum.USER} />
                <NavLabel>Ogłoszenia użytkowników</NavLabel>
            </NavItem>
            <NavItem to="/my-offers">
                <Icon icon={IconEnum.BOOKMARK} />
                <NavLabel>Moje ogłoszenia</NavLabel>
            </NavItem>
            <NavItem to="/add-offer">
                <Icon icon={IconEnum.PLUS_CIRCLE} />
                <NavLabel>Dodaj ogłoszenie</NavLabel>
            </NavItem>
            <NavItem to="/messages">
                <Icon icon={IconEnum.MESSAGE} />
                <NavLabel>Wiadomości</NavLabel>
            </NavItem>
        </SectionItems>

        <Divider />

        <SectionHeader onClick={() => toggleSection('moje')}>
            <Icon icon={IconEnum.HEART_NAV} />
            Moje
            <ChevronIcon $open={openSection === 'moje'}>
                <Icon icon={IconEnum.ACCORDION_DOWN} />
            </ChevronIcon>
        </SectionHeader>
        <SectionItems $open={openSection === 'moje'}>
            <NavItem to="/favorites">
                <Icon icon={IconEnum.HEART_NAV} />
                <NavLabel>Ulubione</NavLabel>
            </NavItem>
            <NavItem to="/chatbot-messages">
                <Icon icon={IconEnum.CHAT_LINES} />
                <NavLabel>Flipchat</NavLabel>
            </NavItem>
        </SectionItems>

        <Divider />

        {user.isInOrganization &&
                <>
                    <SectionHeader onClick={() => toggleSection('posrednik')}>
                        <Icon icon={IconEnum.BRIEFCASE} />
                        Strefa pośrednika
                        <ChevronIcon $open={openSection === 'posrednik'}>
                            <Icon icon={IconEnum.ACCORDION_DOWN} />
                        </ChevronIcon>
                    </SectionHeader>
                    <SectionItems $open={openSection === 'posrednik'}>
                        <NavItem to="/broker">
                            <Icon icon={IconEnum.BRIEFCASE} />
                            <NavLabel>Moje pozyski</NavLabel>
                        </NavItem>
                        <NavButton onClick={() => navigate('/my-settings#panel-posrednika')}>
                            <Icon icon={IconEnum.PEOPLES_3} />
                            <NavLabel>Zarządzanie agentami</NavLabel>
                        </NavButton>
                    </SectionItems>
                    <Divider />
                </>
        }

        <SectionHeader onClick={() => toggleSection('analityka')}>
            <Icon icon={IconEnum.CHART} />
            Analityka
            <ChevronIcon $open={openSection === 'analityka'}>
                <Icon icon={IconEnum.ACCORDION_DOWN} />
            </ChevronIcon>
        </SectionHeader>
        <SectionItems $open={openSection === 'analityka'}>
            <NavItem to="/analysis">
                <Icon icon={IconEnum.CHART} />
                <NavLabel>Analiza cen ofertowych</NavLabel>
            </NavItem>
            {hasCalculationRole &&
                    <NavItem to="/calculator">
                        <Icon icon={IconEnum.CALCULATOR} />
                        <NavLabel>Kalkulator Flipa</NavLabel>
                        <HighlightBadge>NEW</HighlightBadge>
                    </NavItem>
            }
            {hasCalculationRole &&
                    <NavItem to="/analytic-panel">
                        <Icon icon={IconEnum.GRID_2X2} />
                        <NavLabel>Panel Analityczny</NavLabel>
                        <HighlightBadge>NEW</HighlightBadge>
                    </NavItem>
            }
            {hasPredictPriceRole &&
                    <NavItem to="/property-valuation">
                        <Icon icon={IconEnum.SCALE} />
                        <NavLabel>Wycena mieszkań</NavLabel>
                    </NavItem>
            }
            {isWordpressUser && platform === Platform.Web &&
                    <NavButton onClick={() => window.open('https://ceny.investoro.pl/', '_blank')}>
                        <Icon icon={IconEnum.CIRCLE_DOLLAR} />
                        <NavLabel>Ceny mieszkań</NavLabel>
                    </NavButton>
            }
        </SectionItems>

        <Divider />

        <SectionHeader onClick={() => toggleSection('konto')}>
            <Icon icon={IconEnum.COG} />
            Konto
            <ChevronIcon $open={openSection === 'konto'}>
                <Icon icon={IconEnum.ACCORDION_DOWN} />
            </ChevronIcon>
        </SectionHeader>
        <SectionItems $open={openSection === 'konto'}>
            {isWordpressUser && platform === Platform.Web &&
                    <NavButton onClick={() => window.open('https://investoro.pl/userpanel/', '_blank')}>
                        <Icon icon={IconEnum.USER} />
                        <NavLabel>Panel użytkownika</NavLabel>
                    </NavButton>
            }
            <NavItem to="/my-settings">
                <Icon icon={IconEnum.COG} />
                <NavLabel>Ustawienia konta</NavLabel>
            </NavItem>
            {hasCalculationRole &&
                    <NavItem to="/calculator-settings">
                        <Icon icon={IconEnum.CALCULATOR} />
                        <NavLabel>Ustawienia kalkulatora</NavLabel>
                    </NavItem>
            }
            <NavButton onClick={() => UserActions.changeTheme(dispatch, !isDarkMode)}>
                <Icon icon={IconEnum.MOON} />
                <NavLabel>{isDarkMode ? 'Jasny motyw' : 'Ciemny motyw'}</NavLabel>
            </NavButton>
            <NavButton onClick={() => window.open('https://investoro.pl/jak-uzywac/', '_blank')}>
                <Icon icon={IconEnum.QUESTION_CIRCLE} />
                <NavLabel>Jak używać</NavLabel>
            </NavButton>
            {isWordpressUser && platform === Platform.Web &&
                    <NavButton onClick={() => window.open('https://investoro.pl/wsparcie', '_blank')}>
                        <Icon icon={IconEnum.ARROW_UP_RIGHT} />
                        <NavLabel>Pomoc / Zgłoszenia</NavLabel>
                    </NavButton>
            }
            {platform === Platform.Web &&
                    <NavButton onClick={() => window.open('https://play.google.com/store/apps/details?id=com.investoro.app', '_blank')}>
                        <Icon icon={IconEnum.ANDROID} />
                        <NavLabel>Pobierz aplikację Android</NavLabel>
                    </NavButton>
            }
            {platform === Platform.Web &&
                    <NavButton onClick={() => window.open('https://apps.apple.com/pl/app/investoro/id6740319885', '_blank')}>
                        <Icon icon={IconEnum.IOS} />
                        <NavLabel>Pobierz aplikację iOS</NavLabel>
                    </NavButton>
            }
            <NavButton onClick={logout}>
                <Icon icon={IconEnum.LEAVE} />
                <NavLabel $color="var(--color-error)">Wyloguj się</NavLabel>
            </NavButton>
        </SectionItems>
    </ScrollArea>
    , [
        isDarkMode,
        openSection,
        hasCalculationRole,
        hasPredictPriceRole,
        isWordpressUser,
        platform,
        logout,
        dispatch,
        toggleSection,
        navigate
    ]);

    const COLLAPSED_SECTIONS = useMemo(() => [
        { id: 'moduly', icon: IconEnum.HOUSE, label: 'Moduły' },
        { id: 'marketplace', icon: IconEnum.USER, label: 'Market' },
        { id: 'moje', icon: IconEnum.HEART_NAV, label: 'Moje' },
        ...user.isInOrganization ? [{ id: 'posrednik', icon: IconEnum.BRIEFCASE, label: 'Strefa' }] : [],
        { id: 'analityka', icon: IconEnum.CHART, label: 'Analiza' },
        { id: 'konto', icon: IconEnum.COG, label: 'Konto' }
    ], [user.isInOrganization]);

    const stripContent = useMemo(() => <IconStripScroll>
        {COLLAPSED_SECTIONS.map((section) => <CollapsedSectionBtn
            key={section.id}
            $active={openSection === section.id}
            onClick={() => {
                setOpenSection(section.id);
                setCollapsed(false);
            }}
        >
            <Icon icon={section.icon} />
            <CollapsedSectionLabel $active={openSection === section.id}>
                {section.label}
            </CollapsedSectionLabel>
        </CollapsedSectionBtn>)}
    </IconStripScroll>
    , [COLLAPSED_SECTIONS, openSection]);

    const isExpanded = !collapsed || drawerOpen;
    const sidebarWidth = isExpanded ? SIDEBAR_WIDTH : ICON_ONLY_WIDTH;
    const showStrip = !isExpanded;

    return (
        <>
            {collapsed && drawerOpen &&
                <DrawerOverlay
                    onClick={() => setDrawerOpen(false)}
                    onTouchStart={() => setDrawerOpen(false)}
                />
            }
            {isTouch && !collapsed &&
                <DrawerOverlay
                    onClick={() => setCollapsed(true)}
                    onTouchStart={() => setCollapsed(true)}
                />
            }

            <SidebarWrapper
                ref={sidebarRef}
                $width={sidebarWidth}
                className="sidebar-desktop"
                onMouseEnter={!isTouch ? handleMouseEnter : undefined}
                onMouseLeave={!isTouch ? handleMouseLeave : undefined}
                onTouchStart={isTouch && collapsed ? () => setCollapsed(false) : undefined}
            >
                <LogoArea $centered={showStrip}>
                    {showStrip
                        ? <LogoImgFavicon
                            src="/logo_favicon.png"
                            alt="Investoro"
                            className="sidebar-logo"
                            $clickable={platform !== Platform.IOS}
                            onClick={() => {
                                if (platform !== Platform.IOS) window.location.href = 'https://investoro.pl/';
                            }}
                        />
                        : <>
                            <LogoImg
                                src="/Investoro_2021_50px.png"
                                alt="Investoro Logo"
                                className="sidebar-logo"
                                $clickable={platform !== Platform.IOS}
                                onClick={() => {
                                    if (platform !== Platform.IOS) window.location.href = 'https://investoro.pl/';
                                }}
                            />
                            <CollapseButton
                                $collapsed={collapsed}
                                onClick={toggleCollapsed}
                                title={collapsed ? 'Przypnij menu' : 'Odepnij menu'}
                                aria-label={collapsed ? 'Przypnij menu' : 'Odepnij menu'}
                            >
                                <Icon icon={IconEnum.ANGLE_RIGHT} />
                            </CollapseButton>
                        </>
                    }
                </LogoArea>

                {showStrip ? stripContent : navContent}
            </SidebarWrapper>
        </>
    );
};

export default NavigationDesktop;
